import { useMemo, useState, type ReactNode } from "react"
import {
  useQECapture,
  type UseQECapture,
} from "../quick-estimate/use-qe-capture"
import type { QuickEstimateActions } from "../quick-estimate/use-quick-estimate-state"
import { InPageCamera } from "./in-page-camera"

// Bundles the photo-capture pipeline shared by every estimate wizard:
//
//   1. The QE3 capture hook (preflight + AI eval + 3-concurrent limit).
//   2. An in-page camera modal that lets the user snap multiple photos
//      in one open session — replaces the OS camera, which closes
//      after a single shot. Mobile/tablet only.
//   3. A hidden <input type="file"> that serves as the library
//      affordance (and the camera-permission-denied fallback). On
//      desktop it's also the primary entry point, since a webcam-based
//      flow makes no sense for a workshop laptop.
//
// `triggerCapture` on the returned `capture` is overridden so that
// touch-primary devices open the modal and pointer-primary (desktop)
// devices open the OS file picker directly. The retake-after-edit
// path in step-photos calls this same method, so it inherits the
// routing automatically.
//
// Returned as JSX nodes (not raw refs / state) so the wizard owns
// nothing about the camera lifecycle — it just drops `cameraNodes`
// into its render tree.

export interface EstimateCaptureBundle {
  capture: UseQECapture
  cameraNodes: ReactNode
}

// Re-evaluated on each trigger so a docked tablet that gains a mouse
// (or a desktop user plugging in a touch monitor mid-session) still
// gets the right path without remounting the wizard.
function isTouchPrimary(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false
  return window.matchMedia("(pointer: coarse)").matches
}

export function useEstimateCapture(
  actions: QuickEstimateActions,
): EstimateCaptureBundle {
  const baseCapture = useQECapture(actions)
  const [cameraOpen, setCameraOpen] = useState(false)

  const capture = useMemo<UseQECapture>(
    () => ({
      ...baseCapture,
      triggerCapture: () => {
        if (isTouchPrimary()) {
          setCameraOpen(true)
        } else {
          baseCapture.fileInputRef.current?.click()
        }
      },
    }),
    [baseCapture],
  )

  const cameraNodes = (
    <>
      {/* Hidden file input — used by the in-page camera's "Library"
          affordance and the permission-denied fallback. Each picked
          File runs through the same preflight + concurrency-limited
          evaluation pipeline. */}
      <input
        ref={capture.fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={(e) => {
          const files = Array.from(e.target.files ?? [])
          files.forEach((f) => capture.handleFile(f))
          e.target.value = ""
        }}
        className="hidden"
      />
      <InPageCamera
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCapture={capture.handleFile}
        onPickFromLibrary={() => baseCapture.fileInputRef.current?.click()}
      />
    </>
  )

  return { capture, cameraNodes }
}
